export const BADGE_COLORS = {
  // Rarity-based colors (now primary system)
  rarity: {
    common: {
      background: '#28A745', // Green for tier 1 (threshold 7-10)
      color: '#FFFFFF',
      glow: 'rgba(40, 167, 69, 0.3)',
      borderColor: '#34CE57'
    },
    rare: {
      background: '#0044ffff', // Blue for tier 2 (threshold 25-30)
      color: '#FFFFFF', 
      glow: 'rgba(0, 80, 165, 0.3)',
      borderColor: '#1f79ffff'
    },
    epic: {
      background: '#4B0082', // Purple for tier 3 (threshold 100+) - using current Founder style
      color: '#FFFFFF',
      glow: 'rgba(138, 43, 226, 0.7)',
      borderColor: '#8A2BE2',
      secondaryGlow: 'rgba(75, 0, 130, 0.4)'
    },
    legendary: {
      background: '#B8860B', // Gold for special badges - using current Creator style
      color: '#FFFFFF',
      glow: 'rgba(255, 215, 0, 0.6)',
      borderColor: '#FFD700',
      secondaryGlow: 'rgba(184, 134, 11, 0.4)'
    }
  },
  
  // Special achievement types (now mapped to legendary)
  special: {
    'founder': {
      background: '#cc00c2ff', // Same as legendary
      color: '#FFFFFF',
      glow: 'rgba(255, 0, 242, 0.6)',
      borderColor: '#f821ffff',
      secondaryGlow: 'rgba(212, 0, 255, 0.4)'
    },
    'creator': {
      background: '#B8860B', // Same as legendary
      color: '#FFFFFF',
      glow: 'rgba(255, 215, 0, 0.6)',
      borderColor: '#FFD700',
      secondaryGlow: 'rgba(184, 134, 11, 0.4)'
    }
  },

  // Default fallback
  default: {
    background: '#667eea',
    color: '#FFFFFF',
    glow: 'rgba(102, 126, 234, 0.3)'
  }
};

// Badge effect configurations by rarity
export const BADGE_EFFECTS = {
  common: {
    floating: false,
    shadowIntensity: 1,
    glowIntensity: 0,
    borderAnimation: false,
    breathing: false,
    floatDistance: 0,
    breatheScale: 1
  },
  rare: {
    floating: true,
    shadowIntensity: 1.5,
    glowIntensity: 0,
    borderAnimation: true,
    breathing: false,
    floatDistance: 2,
    breatheScale: 1
  },
  epic: {
    floating: true,
    shadowIntensity: 2,
    glowIntensity: 1,
    borderAnimation: true,
    breathing: true,
    floatDistance: 3,
    breatheScale: 1.02
  },
  legendary: {
    floating: true,
    shadowIntensity: 2.5,
    glowIntensity: 1.5,
    borderAnimation: true,
    breathing: true,
    floatDistance: 4,
    breatheScale: 1.03
  }
};

/**
 * Determine achievement rarity based on threshold within category
 * @param {Object} achievement - Achievement object
 * @returns {string} Rarity level ('common', 'rare', 'epic', or 'legendary')
 */
const getAchievementRarity = (achievement) => {
  // Special achievements and user groups are always legendary (most prestigious)
  if (achievement.category === 'user-groups') {
    return 'legendary';
  }

  // For achievements with thresholds, determine rarity
  if (achievement.threshold !== undefined && achievement.threshold !== null) {
    // Daily streak rarities
    if (achievement.category === 'daily-streak') {
      if (achievement.threshold >= 100) return 'epic';
      if (achievement.threshold >= 30) return 'rare';
      return 'common';
    }
    
    // Total wins rarities (same for all games)
    if (achievement.category === 'total-wins') {
      if (achievement.threshold >= 100) return 'epic';
      if (achievement.threshold >= 25) return 'rare';
      return 'common';
    }
  }

  // Activity-based achievements
  if (achievement.category === 'activity') {
    // Flawless Victory (1 guess) is legendary - extremely rare
    if (achievement.name && achievement.name.toLowerCase().includes('flawless')) {
      return 'legendary';
    }
    // Lore Seeker (read all guides) is epic - requires dedication
    if (achievement.name && achievement.name.toLowerCase().includes('lore')) {
      return 'epic';
    }
    // Broken Clock (30+ guesses) is common - happens naturally with difficulty
    if (achievement.name && achievement.name.toLowerCase().includes('broken')) {
      return 'common';
    }
    // Default for activity achievements
    return 'rare';
  }

  // Check by name pattern for special badges
  if (achievement.name) {
    const lowerName = achievement.name.toLowerCase();
    // Look for epic tier indicators in names
    if (lowerName.includes('master') || lowerName.includes('centurion') || 
        lowerName.includes('slayer') || lowerName.includes('strategist') || 
        lowerName.includes('interloper') || lowerName.includes('word')) {
      return 'epic';
    }
    if (lowerName.includes('devotee') || lowerName.includes('conqueror') || 
        lowerName.includes('expert') || lowerName.includes('shark')) {
      return 'rare';
    }
  }

  // Default to rare for unknown achievements
  return 'rare';
};

/**
 * Legacy function for backwards compatibility
 * @param {Object} achievement - Achievement object
 * @returns {number} Tier level (1, 2, or 3)
 */
const getAchievementTier = (achievement) => {
  const rarity = getAchievementRarity(achievement);
  const rarityToTier = {
    'common': 1,
    'rare': 2,
    'epic': 3,
    'legendary': 3
  };
  return rarityToTier[rarity] || 2;
};

/**
 * Get badge color configuration for an achievement based on rarity
 * @param {Object} achievement - Achievement object with category, game_type, etc.
 * @returns {Object} Color configuration with background, color, and glow
 */
export const getBadgeColor = (achievement) => {
  // Check for special achievements first (Creator/Founder are legendary)
  if (achievement.name) {
    const lowerName = achievement.name.toLowerCase().replace(/\s+/g, '-');
    if (BADGE_COLORS.special[lowerName]) {
      return BADGE_COLORS.special[lowerName];
    }
  }

  // Get rarity and return corresponding colors
  const rarity = getAchievementRarity(achievement);
  return BADGE_COLORS.rarity[rarity] || BADGE_COLORS.rarity.common;
};

/**
 * Get CSS custom properties for a badge
 * @param {Object} achievement - Achievement object
 * @returns {Object} CSS custom properties object
 */
export const getBadgeCSSProperties = (achievement) => {
  const colors = getBadgeColor(achievement);
  const rarity = getAchievementRarity(achievement);
  const tier = getAchievementTier(achievement); // For backwards compatibility
  const effects = BADGE_EFFECTS[rarity] || BADGE_EFFECTS.common;
  
  // Check if this is a special premium badge (legendary rarity)
  const isSpecialBadge = rarity === 'legendary' || (achievement.name && 
    ['founder', 'creator'].includes(achievement.name.toLowerCase().replace(/\s+/g, '-')));
  
  return {
    '--badge-bg': colors.background,
    '--badge-color': colors.color,
    '--badge-glow': colors.glow,
    '--badge-tier': tier, // Keep for CSS compatibility
    '--badge-rarity': rarity,
    '--badge-shadow-intensity': effects.shadowIntensity,
    '--badge-glow-intensity': effects.glowIntensity,
    '--badge-float-distance': `${effects.floatDistance}px`,
    '--badge-breathe-scale': effects.breatheScale,
    '--badge-has-floating': effects.floating ? '1' : '0',
    '--badge-has-border-anim': effects.borderAnimation ? '1' : '0',
    '--badge-has-breathing': effects.breathing ? '1' : '0',
    '--badge-is-special': isSpecialBadge ? '1' : '0',
    '--badge-border-color': colors.borderColor || colors.glow,
    '--badge-secondary-glow': colors.secondaryGlow || colors.glow
  };
};

export { getAchievementTier, getAchievementRarity };

export default BADGE_COLORS;